import { Injectable, NgZone } from '@angular/core';
import { record } from 'rrweb';
import { eventWithTime, listenerHandler } from '@rrweb/types';
import * as pako from 'pako';

@Injectable({
  providedIn: 'root'
})
export class ReplayRecorderService {
  private events: eventWithTime[] = [];
  private stopFn: listenerHandler | null = null;
  private isRecording = false;
  private windowDurationMs = 60 * 1000; // 60 seconds circular buffer
  private checkoutIntervalMs = 25 * 1000; // Take full DOM snapshot every 25 seconds

  constructor(private ngZone: NgZone) {}

  /**
   * Start recording DOM events in a background circular memory buffer.
   */
  public startRecording(): void {
    if (this.isRecording) return;
    if (typeof window === 'undefined') return;

    this.isRecording = true;
    this.events = [];

    // Run outside Angular zone so rrweb DOM mutation listeners don't trigger Change Detection
    this.ngZone.runOutsideAngular(() => {
      try {
        this.stopFn = record({
          emit: (event, isCheckout) => {
            this.handleNewEvent(event, isCheckout);
          },
          checkoutEveryNms: this.checkoutIntervalMs,
          maskAllInputs: true,
          maskInputOptions: {
            password: true,
            text: true,
            email: false
          },
          // Exclude any heavy elements or sensitive classes if tagged
          blockClass: 'rr-block',
          maskTextClass: 'rr-mask',
          sampling: {
            mousemove: true,
            mouseInteraction: true,
            scroll: 150, // Throttle scroll events (ms)
            input: 'last' // Only send last input value
          }
        });
      } catch (err) {
        console.warn('[ReplayRecorderService] Failed to initialize rrweb recording:', err);
        this.isRecording = false;
      }
    });
  }

  /**
   * Stop recording
   */
  public stopRecording(): void {
    if (this.stopFn) {
      this.stopFn();
      this.stopFn = null;
    }
    this.isRecording = false;
  }

  /**
   * Check if recording is active
   */
  public isActive(): boolean {
    return this.isRecording;
  }

  /**
   * Event count currently in buffer
   */
  public getEventCount(): number {
    return this.events.length;
  }

  private handleNewEvent(event: eventWithTime, isCheckout?: boolean): void {
    this.events.push(event);
    this.pruneBuffer();
  }

  /**
   * Prune events older than windowDurationMs while ensuring the stream
   * always begins with a FullSnapshot (type 2) so replay doesn't break.
   */
  private pruneBuffer(): void {
    if (this.events.length === 0) return;

    const now = Date.now();
    const cutoffTime = now - this.windowDurationMs;

    // Find all full snapshots (type 2)
    const snapshotIndices: number[] = [];
    for (let i = 0; i < this.events.length; i++) {
      if (this.events[i].type === 2) {
        snapshotIndices.push(i);
      }
    }

    if (snapshotIndices.length <= 1) {
      // Keep everything if we only have 1 or fewer snapshots
      return;
    }

    // Find the latest snapshot that occurred before or close to cutoffTime
    let targetSnapshotIndex = 0;
    for (let i = snapshotIndices.length - 1; i >= 0; i--) {
      const idx = snapshotIndices[i];
      if (this.events[idx].timestamp <= cutoffTime) {
        targetSnapshotIndex = idx;
        break;
      }
    }

    // If all snapshots are older than cutoff, or newer, keep from targetSnapshotIndex
    if (targetSnapshotIndex > 0) {
      this.events = this.events.slice(targetSnapshotIndex);
    }
  }

  /**
   * Returns the raw events array
   */
  public getEvents(): eventWithTime[] {
    return [...this.events];
  }

  /**
   * Returns duration of currently buffered recording in seconds
   */
  public getBufferDurationSeconds(): number {
    if (this.events.length < 2) return 0;
    const first = this.events[0].timestamp;
    const last = this.events[this.events.length - 1].timestamp;
    return Math.max(0, Math.round((last - first) / 1000));
  }

  /**
   * Returns gzip compressed and base64 encoded string of events
   */
  public getCompressedReplay(): { base64: string; rawSize: number; compressedSize: number } {
    try {
      const json = JSON.stringify(this.events);
      const rawSize = json.length;
      const gzipped = pako.gzip(json);
      const compressedSize = gzipped.length;

      // Convert Uint8Array to binary string chunk by chunk
      let binary = '';
      const chunkSize = 8192;
      for (let i = 0; i < gzipped.length; i += chunkSize) {
        const chunk = gzipped.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, Array.from(chunk));
      }
      const base64 = btoa(binary);

      return {
        base64,
        rawSize,
        compressedSize
      };
    } catch (e) {
      console.error('[ReplayRecorderService] Error compressing replay data', e);
      return { base64: '', rawSize: 0, compressedSize: 0 };
    }
  }

  /**
   * Clear buffer
   */
  public clear(): void {
    this.events = [];
  }
}
